import { range } from 'lodash';
import {
  AvailableBlockDentistForSplitScheduling,
  AvailableBlockOperatory,
  IAvailableBlocks,
  SearchingService,
} from 'hooks/usePractitionerWeeklyTimeSlot';
import { EXAM_PLACEMENT } from 'utils/constants';
import { IPractitioner } from 'interfaces/practitioners';

export const BOOKING_SPILT_SCHEDULING_TYPE = {
  HEAD: 'head' as const, // Book dentist first, then hygienist
  TAIL: 'tail' as const, // Book hygienist first, then dentist
};

export const getOperatoryId = ({
  selectedTimeSlot,
  availableBlocksBySelectedDoctor,
}: {
  selectedTimeSlot: {
    date: string;
    value: number;
  };
  availableBlocksBySelectedDoctor: IAvailableBlocks[];
}) => {
  let operatoryId = null;

  const availableBlocksBySelectedDate =
    availableBlocksBySelectedDoctor.find(
      (item) => item.date === selectedTimeSlot!.date
    ) || null;

  if (availableBlocksBySelectedDate) {
    const availableOperatoriesBySelectedSlot =
      availableBlocksBySelectedDate.availableBlockOperatories?.find((item) =>
        item.blocks.includes(selectedTimeSlot!.value)
      );

    operatoryId = availableOperatoriesBySelectedSlot?.operatoryId || null;
  }

  return operatoryId;
};

const isFirstContainedBySecondBlockNumbers = (
  firstBlockNumbers: number[],
  secondBlockNumbers: number[]
) => {
  return firstBlockNumbers.every((item) => secondBlockNumbers.includes(item));
};

export const getDentistIdForSplitScheduling = ({
  date,
  timeSlot,
  availableBlocks,
}: {
  date: string;
  timeSlot: number;
  availableBlocks: {
    date: string;
    availableBlockOperatories?: AvailableBlockOperatory[];
    blocks: number[];
    searchingService: SearchingService;
    dentists?: AvailableBlockDentistForSplitScheduling[];
  }[];
}) => {
  const availableBlock = availableBlocks.find((item) => item.date === date);

  const searchingService = availableBlock?.searchingService;

  const doctorDuration = searchingService?.doctorDuration || 0;
  const hygienistDuration = searchingService?.hygienistDuration || 0;

  const headRange = range(timeSlot, timeSlot + doctorDuration);
  const tailRange = range(
    timeSlot + hygienistDuration,
    timeSlot + hygienistDuration + doctorDuration
  );

  const dentists = availableBlock?.dentists || [];

  const isExistingHead = dentists.some((item) =>
    isFirstContainedBySecondBlockNumbers(
      headRange,
      item.headAvailableBlockNumbers
    )
  );

  const isExistingTail = dentists.some((item) =>
    isFirstContainedBySecondBlockNumbers(
      tailRange,
      item.tailAvailableBlockNumbers
    )
  );

  if (isExistingHead && isExistingTail) {
    return {
      id: null,
      timeSlot,
      timeSlotType: null,
    };
  }

  if (isExistingHead && !isExistingTail) {
    return {
      id: null,
      timeSlot,
      timeSlotType: EXAM_PLACEMENT.HEAD,
    };
  }

  if (isExistingTail && !isExistingHead) {
    return {
      id: null,
      timeSlot,
      timeSlotType: EXAM_PLACEMENT.TAIL,
    };
  }

  return {
    id: null,
    timeSlot,
    timeSlotType: null,
  };
};

export const NO_PREFERENCE_OPTION = {
  id: 'no-preference' as const,
  label: 'No preference' as const,
};

export const isNoPreferenceOption = (
  option: IPractitioner | typeof NO_PREFERENCE_OPTION
): option is typeof NO_PREFERENCE_OPTION => 'label' in option;
